import React, { useState } from 'react';
import { motion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faGavel,
  faShieldHalved,
  faLock,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { revealStagger } from '../../utils/motion';
import DarkVeil from './DarkVeil';

export default function FeaturesSection() {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.15 });
  const [fractionPercent, setFractionPercent] = useState(25);

  const baseValuation = 4000000; // NPR 4,000,000
  const ownedValue = Math.round((baseValuation * fractionPercent) / 100);
  const dividendEstimate = Math.round(ownedValue * 0.145);

  return (
    <section className="lp-features" ref={ref}>
      <div className="lp-features-bg">
        <DarkVeil
          hueShift={0}
          noiseIntensity={0}
          scanlineIntensity={0}
          speed={5}
          scanlineFrequency={0}
          warpAmount={0}
        />
      </div>
      <div className="lp-container lp-features-content">
        <div className="lp-section-header">
          <span className="lp-section-badge">Platform Architecture</span>
          <h2 className="lp-section-title">
            Built for the <span className="lp-gradient-text">Future of Art</span>
          </h2>
          <p className="lp-section-desc">
            Transcend traditional gallery limitations with institutional-grade fractional tokenization,
            real-time order books, and cryptographic provenance guarantees.
          </p>
        </div>

        {/* Dynamic Bento Layout (Breaking the traditional box design) */}
        <motion.div
          className="lp-bento-layout"
          variants={revealStagger.container}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
        >
          {/* Card 1: Wide Interactive Fractional Tokenizer */}
          <motion.div
            className="lp-bento-card lp-bento-wide"
            variants={revealStagger.item}
          >
            <div className="lp-bento-card-header">
              <div className="lp-bento-icon-pill">
                <FontAwesomeIcon icon={faChartPie} />
              </div>
              <span className="lp-bento-tag">TitTat Fractional Engine</span>
            </div>

            <div className="lp-bento-body">
              <h3 className="lp-bento-title">Algorithmic Fractionalization</h3>
              <p className="lp-bento-desc">
                High-value blue chip masterpieces are tokenized into 100 to 10,000 TitTat units.
                Investors can trade fractions instantly with guaranteed liquidity pools.
              </p>

              {/* Interactive Fraction Simulator */}
              <div className="lp-fraction-simulator">
                <div className="lp-simulator-header">
                  <span className="lp-sim-label">Fractional Ownership Stake</span>
                  <span className="lp-sim-val font-mono">{fractionPercent}% of Masterpiece</span>
                </div>

                <div className="lp-slider-track-wrap">
                  <input
                    type="range"
                    min="5"
                    max="100"
                    step="5"
                    value={fractionPercent}
                    onChange={(e) => setFractionPercent(Number(e.target.value))}
                    className="lp-fraction-slider"
                  />
                  <div
                    className="lp-slider-fill"
                    style={{ width: `${fractionPercent}%` }}
                  />
                </div>

                <div className="lp-sim-results-grid">
                  <div className="lp-sim-metric">
                    <span className="lp-metric-label">Token Equity Value</span>
                    <span className="lp-metric-val font-mono">
                      NPR {ownedValue.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="lp-sim-metric">
                    <span className="lp-metric-label">Estimated Annual Yield (14.5%)</span>
                    <span className="lp-metric-val font-mono" style={{ color: 'var(--success, #10b981)' }}>
                      +NPR {dividendEstimate.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card 2: Live Auction Engine with Simulated Bid Stream */}
          <motion.div
            className="lp-bento-card lp-bento-tall"
            variants={revealStagger.item}
          >
            <div className="lp-bento-card-header">
              <div className="lp-bento-icon-pill">
                <FontAwesomeIcon icon={faGavel} />
              </div>
              <span className="lp-bento-tag">Sub-10ms Auctions</span>
            </div>

            <div className="lp-bento-body">
              <h3 className="lp-bento-title">WebSocket Live Bidding</h3>
              <p className="lp-bento-desc">
                Optimistic Concurrency Control (OCC) and anti-sniping dynamic soft-close windows.
              </p>

              {/* Real Art Lot Preview */}
              <div className="lp-bento-art-preview">
                <img src="/image_2.png" alt="Silent Resonance" className="lp-bento-art-thumb" />
                <div className="lp-bento-art-meta">
                  <span className="lp-bento-lot-title font-mono">LOT #02: Silent Resonance</span>
                  <span className="lp-bento-lot-reserve">Reserve Met • TitTat #02</span>
                </div>
              </div>

              <div className="lp-sim-bid-feed">
                <div className="lp-bid-feed-item top-bid">
                  <div className="lp-bidder-avatar">
                    <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b', fontSize: '11px' }} />
                  </div>
                  <div className="lp-bid-info">
                    <span className="lp-bid-user">Investor_904</span>
                    <span className="lp-bid-time">Just now</span>
                  </div>
                  <span className="lp-bid-amount font-mono">NPR 1,420,000</span>
                </div>
                <div className="lp-bid-feed-item">
                  <div className="lp-bidder-avatar">
                    <FontAwesomeIcon icon={faShieldHalved} style={{ color: '#06b6d4', fontSize: '11px' }} />
                  </div>
                  <div className="lp-bid-info">
                    <span className="lp-bid-user">Collector_Kathmandu</span>
                    <span className="lp-bid-time">12s ago</span>
                  </div>
                  <span className="lp-bid-amount font-mono">NPR 1,350,000</span>
                </div>
                <div className="lp-bid-feed-item">
                  <div className="lp-bidder-avatar">
                    <FontAwesomeIcon icon={faLock} style={{ color: '#a855f7', fontSize: '11px' }} />
                  </div>
                  <div className="lp-bid-info">
                    <span className="lp-bid-user">Himalayan_Fund</span>
                    <span className="lp-bid-time">45s ago</span>
                  </div>
                  <span className="lp-bid-amount font-mono">NPR 1,280,000</span>
                </div>
              </div>

              <div className="lp-auction-status-bar">
                <span className="lp-status-live-pulse" />
                <span>Next Soft-Close Extension: +120s</span>
              </div>
            </div>
          </motion.div>

          {/* Card 3: Cryptographic Provenance & Settlement Vault */}
          <motion.div
            className="lp-bento-card lp-bento-tall"
            variants={revealStagger.item}
          >
            <div className="lp-bento-card-header">
              <div className="lp-bento-icon-pill">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <span className="lp-bento-tag">Institutional Vault</span>
            </div>

            <div className="lp-bento-body">
              <h3 className="lp-bento-title">Immutable Provenance</h3>
              <p className="lp-bento-desc">
                Every TitTat fraction maps directly to an authenticated legal management accord.
              </p>

              <div className="lp-vault-seal-display">
                <div className="lp-vault-seal-icon">
                  <FontAwesomeIcon icon={faLock} />
                </div>
                <div className="lp-vault-seal-details">
                  <span className="lp-seal-status">CRPT-SEAL #9981-SHA256</span>
                  <span className="lp-seal-hash font-mono">
                    0x7f9a...3c81e2b4
                  </span>
                </div>
              </div>

              <div className="lp-vault-features-list">
                <div className="lp-vault-check-item">
                  <span>✓ Smart Escrow Multi-Sig</span>
                </div>
                <div className="lp-vault-check-item">
                  <span>✓ 100% Artist Royalty Protection</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
